import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/asset-items")({
  component: RouteComponent,
});

function RouteComponent() {
  // AssetItemViewSet's read_permission ("inventory.stock.view") gates list
  // and retrieve too, with no degrade-to-own-rows fallback, so the whole
  // page is protected up front; write_permission ("inventory.rental.manage")
  // gates just the write UI within (see canManage below).
  return (
    <ProtectedRoute requiredPermission="inventory.stock.view">
      <AssetItemsPage />
    </ProtectedRoute>
  );
}

import { useState } from "react";
import { toast } from "sonner";
import {
  Search,
  FilterIcon,
  MoreHorizontal,
  Tag,
  FileDown,
  CheckSquare,
  X,
  RotateCcw,
} from "lucide-react";
import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DashboardHeader } from "@/components/dashboard-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { BarcodeScanControl } from "@/components/inventory/barcode/BarcodeScanControl";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import {
  useFetchAssetItems,
  useFetchAssetItemStatusCounts,
  useAssetItemMutations,
} from "@/hooks/api/inventory/useAssetItems";
import { cn } from "@/lib/utils";
import { splitAssetItemStatusFilter } from "@/lib/inventory";
import {
  useFetchWarehouses,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import {
  useFetchInspectionChecklists,
  useFetchInspectionFrequencies,
  useFetchLabels,
  useAssetItemAssignMutations,
} from "@/hooks/api/inventory/useCollectionsLabels";
import { AssetItemTable } from "@/components/inventory/tables/AssetItemTable";
import { AssetItemFormSheet } from "@/components/inventory/forms/AssetItemFormSheet";
import { InspectReturnModal } from "@/components/inventory/forms/InspectReturnModal";
import { AssignLabelsModal } from "@/components/inventory/forms/AssignLabelsModal";
import { ReturnManifestModal } from "@/components/inventory/forms/ReturnManifestModal";
import { ExportAssetItemsDialog } from "@/components/inventory/forms/ExportAssetItemsDialog";
import { ItemSearchFilter } from "@/components/inventory/shared/ItemSearchFilter";
import { AssetItemDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import type {
  AssetItem,
  AssetItemPayload,
  AssignLabelsPayload,
  InspectReturnPayload,
  SetInspectionFrequencyPayload,
} from "@/types/inventory";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

const MAX_BULK_LABELS = 100;
const LABEL_ASSIGN_BATCH_SIZE = 5;

// "Not Safe" and "Monitoring" are both real lifecycle statuses now --
// distinct from Quarantined/Maintenance respectively (a Not Safe unit
// failed outright, not merely flagged for repair like a Quarantine; a
// Monitoring-flagged unit is still in service, just watched closely,
// unlike Maintenance's physical pull for servicing). Every value here is
// still sent as `?effective_status=` uniformly (never plain `?status=`),
// via splitAssetItemStatusFilter -- that's what keeps a card's own count
// (computed from effective_status, same as every badge renders) and what
// clicking it actually returns in agreement. Lifecycle `status` can still
// lag the truth in one specific case even now: a unit already Quarantined,
// then found Not Safe by a *later* inspection, stays lifecycle-Quarantined
// (sync_lifecycle_status_from_safety_outcome's "sticky until a clean
// result" rule -- see docs/asset-safety-status-sync-plan.md) while
// effective_status correctly reads the newer, more severe finding. A plain
// `?status=QUARANTINED` would still catch that stale row; effective_status
// never does.
const ASSET_STATUS_OPTIONS = [
  { label: "Available", value: "AVAILABLE" },
  { label: "Reserved", value: "RESERVED" },
  { label: "Deployed", value: "DEPLOYED" },
  { label: "In Inspection", value: "IN_INSPECTION" },
  { label: "Quarantined", value: "QUARANTINED" },
  { label: "Maintenance", value: "MAINTENANCE" },
  { label: "Monitoring", value: "MONITORING" },
  { label: "Not Safe", value: "NOT_SAFE" },
];

// Most attention-needing first, same order the list itself now defaults to
// (see AssetItemViewSet's severity annotation) -- clicking a card filters
// the table straight to that status. NOT_SAFE leads even Quarantined: both
// rank in the backend's top severity tier for sorting, but a NOT_SAFE unit
// is a confirmed failed inspection, not just a precaution.
const STATUS_STAT_CARDS = [
  {
    label: "Not Safe",
    value: "NOT_SAFE",
    colorClass: "text-red-700 dark:text-red-500",
  },
  {
    label: "Quarantined",
    value: "QUARANTINED",
    colorClass: "text-red-600 dark:text-red-400",
  },
  {
    label: "In Inspection",
    value: "IN_INSPECTION",
    colorClass: "text-orange-600 dark:text-orange-400",
  },
  {
    label: "Maintenance",
    value: "MAINTENANCE",
    colorClass: "text-zinc-500 dark:text-zinc-400",
  },
  {
    label: "Monitoring",
    value: "MONITORING",
    colorClass: "text-amber-500 dark:text-amber-400",
  },
  {
    label: "Reserved",
    value: "RESERVED",
    colorClass: "text-amber-600 dark:text-amber-400",
  },
  {
    label: "Deployed",
    value: "DEPLOYED",
    colorClass: "text-blue-600 dark:text-blue-400",
  },
  {
    label: "Available",
    value: "AVAILABLE",
    colorClass: "text-green-600 dark:text-green-400",
  },
] as const;

function AssetItemsPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "inventory.rental.manage");
  // Mirrors ItemViewSet/AssetItemViewSet's export action, which is its own
  // capability (inventory.report.view), deliberately not part of rent.manage.
  const canExport = can(capabilities, "inventory.report.view");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // ── Selection / bulk actions ──────────────────────────────────────────────
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectionPurpose, setSelectionPurpose] = useState<"label" | "export">(
    "label"
  );
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkLabelOpen, setIsBulkLabelOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportScope, setExportScope] = useState<"all" | "selected">("all");

  const [showFilters, setShowFilters] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [itemFilter, setItemFilter] = useState<string | undefined>(undefined);
  const [labelFilter, setLabelFilter] = useState<string | undefined>(undefined);
  // OWNED vs RENTED_IN (rented in from an external vendor).
  const [ownershipFilter, setOwnershipFilter] = useState<string | undefined>(
    undefined
  );

  const resetFilters = () => {
    setSearchFilter("");
    setWarehouseFilter(undefined);
    setStatusFilter(undefined);
    setItemFilter(undefined);
    setLabelFilter(undefined);
    setOwnershipFilter(undefined);
    setPage(1);
  };

  const statusFilterParams = splitAssetItemStatusFilter(statusFilter);

  const assetItemsQuery = useFetchAssetItems({
    page_size: pageSize,
    page,
    search: searchFilter || undefined,
    warehouse: warehouseFilter ? parseInt(warehouseFilter) : undefined,
    ...statusFilterParams,
    item: itemFilter ? parseInt(itemFilter) : undefined,
    labels: labelFilter ? parseInt(labelFilter) : undefined,
    ownership: ownershipFilter,
  });
  const assetItems = assetItemsQuery.data?.results ?? [];

  // Same item/warehouse/search scoping as the list itself, minus status --
  // a breakdown filtered down to one status would defeat the point of it.
  const statusCountsQuery = useFetchAssetItemStatusCounts({
    search: searchFilter || undefined,
    warehouse: warehouseFilter ? parseInt(warehouseFilter) : undefined,
    item: itemFilter ? parseInt(itemFilter) : undefined,
    // Scoped too, or a card would tally units the table can't show -- the
    // same "count and click-through must agree" rule as
    // splitAssetItemStatusFilter.
    ownership: ownershipFilter,
  });
  const statusCounts = statusCountsQuery.data;
  const count = assetItemsQuery.data?.count ?? 0;

  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];

  const { updateAssetItem, inspectReturn, setAssetInspectionSchedule } =
    useAssetItemMutations();
  const { assignLabels } = useAssetItemAssignMutations();
  const checklistsQuery = useFetchInspectionChecklists(ALL_ACTIVE_PARAMS);
  const checklists = checklistsQuery.data?.results ?? [];
  const frequenciesQuery = useFetchInspectionFrequencies({
    ordering: "name",
    page_size: 100,
  });
  const frequencies = frequenciesQuery.data?.results ?? [];
  const labelsQuery = useFetchLabels(ALL_ACTIVE_PARAMS);
  const labels = labelsQuery.data?.results ?? [];

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState<AssetItem | null>(null);
  const [viewing, setViewing] = useState<AssetItem | null>(null);
  const [inspectTarget, setInspectTarget] = useState<AssetItem | null>(null);
  const [assignLabelsTarget, setAssignLabelsTarget] =
    useState<AssetItem | null>(null);
  const [isReturnManifestOpen, setIsReturnManifestOpen] = useState(false);

  const isMutating =
    updateAssetItem.isPending ||
    inspectReturn.isPending ||
    setAssetInspectionSchedule.isPending ||
    assignLabels.isPending;
  const isLoading = assetItemsQuery.isPending || isMutating;
  const isFetching = assetItemsQuery.isFetching || isMutating;

  const handleSubmit = async (data: AssetItemPayload) => {
    if (!editing) return;
    await updateAssetItem.mutateAsync({ id: editing.id, payload: data });
    toast.success("Asset item updated");
    setIsSheetOpen(false);
  };

  const handleSetAssetSchedule = async (
    data: SetInspectionFrequencyPayload
  ) => {
    if (!editing) return;
    await setAssetInspectionSchedule.mutateAsync({
      id: editing.id,
      payload: data,
    });
  };

  const handleInspectReturn = async (payload: InspectReturnPayload) => {
    if (!inspectTarget) return;
    await inspectReturn.mutateAsync({ id: inspectTarget.id, payload });
    toast.success("Return inspection recorded");
    setInspectTarget(null);
  };

  const handleAssignLabels = async (payload: AssignLabelsPayload) => {
    if (!assignLabelsTarget) return;
    await assignLabels.mutateAsync({
      assetItemId: assignLabelsTarget.id,
      payload,
    });
    toast.success("Labels updated");
    setAssignLabelsTarget(null);
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const allSelected =
      assetItems.length > 0 && assetItems.every((a) => selectedIds.has(a.id));
    setSelectedIds(
      allSelected ? new Set() : new Set(assetItems.map((a) => a.id))
    );
  };

  const startSelection = (purpose: "label" | "export") => {
    setSelectionPurpose(purpose);
    setSelectionMode(true);
  };

  const cancelSelection = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkAssignLabels = async (payload: AssignLabelsPayload) => {
    if (selectedIds.size > MAX_BULK_LABELS) {
      toast.error(
        `You can assign labels to at most ${MAX_BULK_LABELS} units at a time.`
      );
      return;
    }
    const ids = Array.from(selectedIds);
    for (let i = 0; i < ids.length; i += LABEL_ASSIGN_BATCH_SIZE) {
      await Promise.all(
        ids
          .slice(i, i + LABEL_ASSIGN_BATCH_SIZE)
          .map((id) => assignLabels.mutateAsync({ assetItemId: id, payload }))
      );
    }
    toast.success(
      `Labels assigned to ${selectedIds.size} unit${selectedIds.size > 1 ? "s" : ""}`
    );
    setIsBulkLabelOpen(false);
    cancelSelection();
  };

  const openExport = (scope: "all" | "selected") => {
    setExportScope(scope);
    setIsExportOpen(true);
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold">Asset Items</h1>
          <div className="flex items-center gap-2">
            {!selectionMode ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters((v) => !v)}
                  disabled={isFetching}
                >
                  <FilterIcon className="h-4 w-4" />
                </Button>
                {(canManage || canExport) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={isFetching}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canManage && (
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => startSelection("label")}
                        >
                          <Tag className="mr-2 h-4 w-4" /> Select to Assign
                          Labels
                        </DropdownMenuItem>
                      )}
                      {canManage && (
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => setIsReturnManifestOpen(true)}
                        >
                          <RotateCcw className="mr-2 h-4 w-4" /> Return Manifest
                          Items
                        </DropdownMenuItem>
                      )}
                      {canExport && (
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => startSelection("export")}
                        >
                          <CheckSquare className="mr-2 h-4 w-4" /> Select to
                          Export Excel
                        </DropdownMenuItem>
                      )}
                      {canExport && (
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => openExport("all")}
                        >
                          <FileDown className="mr-2 h-4 w-4" /> Export to Excel
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelSelection}
                  disabled={isFetching}
                >
                  <X className="h-4 w-4" /> Cancel
                </Button>
                {selectionPurpose === "label" ? (
                  <Button
                    size="sm"
                    onClick={() => setIsBulkLabelOpen(true)}
                    disabled={isFetching || selectedIds.size === 0}
                  >
                    <Tag className="h-4 w-4" />
                    Assign Labels
                    {selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => openExport("selected")}
                    disabled={isFetching || selectedIds.size === 0}
                  >
                    <FileDown className="h-4 w-4" />
                    Export
                    {selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {STATUS_STAT_CARDS.map((card) => (
            <button
              key={card.value}
              type="button"
              onClick={() => {
                setStatusFilter((prev) =>
                  prev === card.value ? undefined : card.value
                );
                setPage(1);
              }}
              className={cn(
                "rounded-lg border p-3 text-center transition-colors hover:bg-muted/40",
                statusFilter === card.value && "border-primary bg-primary/5"
              )}
            >
              <div
                className={cn(
                  "text-2xl font-semibold tabular-nums",
                  card.colorClass
                )}
              >
                {statusCountsQuery.isPending ? (
                  <Skeleton className="h-7 w-10 mx-auto" />
                ) : (
                  (statusCounts?.[card.value] ?? 0)
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {card.label}
              </div>
            </button>
          ))}
        </div>

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by asset ID, item, or SKU..."
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setPage(1);
              }}
              className="h-9 pl-8"
            />
          </div>
          <ItemSearchFilter
            value={itemFilter}
            onChange={(id) => {
              setItemFilter(id == null ? undefined : String(id));
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Warehouse"
            options={warehouses.map((w) => ({
              label: w.name,
              value: String(w.id),
            }))}
            value={warehouseFilter}
            onChange={(v) => {
              setWarehouseFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Status"
            options={ASSET_STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Ownership"
            options={[
              { label: "Owned", value: "OWNED" },
              { label: "Rental / Rented Asset", value: "RENTED_IN" },
            ]}
            value={ownershipFilter}
            onChange={(v) => {
              setOwnershipFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Label"
            options={labels.map((l) => ({
              label: l.name,
              value: String(l.id),
            }))}
            value={labelFilter}
            onChange={(v) => {
              setLabelFilter(v);
              setPage(1);
            }}
          />
          <BarcodeScanControl
            onScan={(code) => {
              setSearchFilter(code.trim());
              setPage(1);
            }}
            dialogDescription="Point the camera at an asset tag, item, or variant barcode to search for it."
          />
        </FiltersPanel>

        <AssetItemTable
          assetItems={assetItems}
          isLoading={isLoading}
          onNameClick={(item) =>
            navigate({
              to: "/admin/inventory/asset-items/$id",
              params: { id: String(item.id) },
            })
          }
          onView={(item) => setViewing(item)}
          onEdit={(item) => {
            setEditing(item);
            setIsSheetOpen(true);
          }}
          onInspectReturn={(item) => setInspectTarget(item)}
          onAssignLabels={(item) => setAssignLabelsTarget(item)}
          canManage={canManage}
          selectionMode={selectionMode}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleAll={toggleAll}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>

      <AssetItemDetailSheet
        isOpen={!!viewing}
        onClose={() => setViewing(null)}
        item={viewing}
      />

      {editing && (
        <AssetItemFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          onSetSchedule={handleSetAssetSchedule}
          editing={editing}
          warehouses={warehouses}
          frequencies={frequencies}
          isLoading={updateAssetItem.isPending}
          isScheduleSaving={setAssetInspectionSchedule.isPending}
        />
      )}

      <InspectReturnModal
        isOpen={!!inspectTarget}
        onClose={() => setInspectTarget(null)}
        onSubmit={handleInspectReturn}
        assetItem={inspectTarget}
        checklists={checklists}
        isLoading={inspectReturn.isPending}
      />

      <AssignLabelsModal
        isOpen={!!assignLabelsTarget}
        onClose={() => setAssignLabelsTarget(null)}
        onSubmit={handleAssignLabels}
        entity={assignLabelsTarget}
        subtitle={
          assignLabelsTarget
            ? `${assignLabelsTarget.asset_identifier} — ${assignLabelsTarget.item_name}`
            : undefined
        }
        allLabels={labels}
        isLoading={assignLabels.isPending}
      />

      <AssignLabelsModal
        isOpen={isBulkLabelOpen}
        onClose={() => setIsBulkLabelOpen(false)}
        onSubmit={handleBulkAssignLabels}
        entity={null}
        bulkCount={selectedIds.size}
        allLabels={labels}
        isLoading={assignLabels.isPending}
      />

      <ReturnManifestModal
        isOpen={isReturnManifestOpen}
        onClose={() => setIsReturnManifestOpen(false)}
      />

      <ExportAssetItemsDialog
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        warehouses={warehouses}
        labels={labels}
        selectedIds={selectedIds}
        onlySelected={exportScope === "selected"}
        initial={{
          search: searchFilter,
          item: itemFilter,
          warehouse: warehouseFilter,
          ...statusFilterParams,
          label: labelFilter,
          ownership: ownershipFilter,
        }}
      />
    </SidebarInset>
  );
}
